import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PageHead, Tabs, Tiles, fmt } from "./Panel/kit";
import { getSetting, handleSwitch, updateSetting } from "../store/setting/setting.action";
import { setToast } from "../util/toast";

const hint = { fontSize: 12, color: "#6B7280", marginTop: 4 };
const label = { fontSize: 13, fontWeight: 600, color: "#374151" };

/// Tek bir ayar alani: baslik, aciklama ve giris kutusu.
const Field = ({ title, note, children, wide }) => (
  <div className={wide ? "col-12" : "col-md-6"} style={{ marginBottom: 18 }}>
    <label style={label}>{title}</label>
    {children}
    {note && <div style={hint}>{note}</div>}
  </div>
);

const Text = (props) => <input {...props} className="form-control" style={{ marginTop: 6 }} />;

const Area = (props) => <textarea {...props} className="form-control" style={{ marginTop: 6 }} />;

/// Gizli anahtarlar varsayilan olarak yildizli gorunur.
const Secret = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="d-flex" style={{ gap: 6, marginTop: 6 }}>
      <input
        className="form-control"
        type={show ? "text" : "password"}
        value={value || ""}
        placeholder={placeholder}
        onChange={onChange}
      />
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShow(!show)}>
        {show ? "Gizle" : "Göster"}
      </button>
    </div>
  );
};

/// Ac / kapa anahtari.
const Toggle = ({ on, onClick, title, note }) => (
  <div className="card" style={{ marginBottom: 12 }}>
    <div className="card-body d-flex justify-content-between align-items-center">
      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div style={hint}>{note}</div>
      </div>
      <button
        type="button"
        className={`btn btn-sm ${on ? "btn-success" : "btn-outline-secondary"}`}
        onClick={onClick}
        style={{ minWidth: 88 }}
      >
        {on ? "Açık" : "Kapalı"}
      </button>
    </div>
  </div>
);

const Card = ({ title, note, children, onSave }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div className="card-body">
      <div style={{ marginBottom: 14 }}>
        <h5 style={{ margin: 0 }}>{title}</h5>
        {note && <div style={hint}>{note}</div>}
      </div>
      <div className="row">{children}</div>
      {onSave && (
        <button type="button" className="btn btn-primary btn-sm" onClick={onSave}>
          Kaydet
        </button>
      )}
    </div>
  </div>
);

const AppSetting = () => {
  const dispatch = useDispatch();
  const { setting } = useSelector((state) => state.setting);

  const [tab, setTab] = useState("genel");
  const [f, setF] = useState({});

  useEffect(() => {
    dispatch(getSetting());
  }, [dispatch]);

  useEffect(() => {
    if (!setting) return;
    setF({
      welcomeMessage: setting.welcomeMessage || "",
      loginBonus: setting.loginBonus ?? "",
      coinPerDollar: setting.coinPerDollar ?? "",
      coinCharge: setting.coinCharge ?? "",
      diamondPerDollar: setting.diamondPerDollar ?? "",
      diamondCharge: setting.diamondCharge ?? "",
      withdrawLimit: setting.withdrawLimit ?? "",
      chargeForRandomCall: setting.chargeForRandomCall ?? "",
      chargeForPrivateCall: setting.chargeForPrivateCall ?? "",
      agoraKey: setting.agoraKey || "",
      agoraCertificate: setting.agoraCertificate || "",
      privacyPolicyLink: setting.privacyPolicyLink || "",
      privacyPolicyText: setting.privacyPolicyText || "",
      termAndCondition: setting.termAndCondition || "",
      redirectAppUrl: setting.redirectAppUrl || "",
      redirectMessage: setting.redirectMessage || "",
      privateKey: setting.privateKey ? JSON.stringify(setting.privateKey, null, 2) : "",
    });
  }, [setting]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  /// Yalniz o bolumun alanlarini gonderir; sunucu gonderilmeyeni degistirmez.
  const save = (keys) => {
    const body = {};
    for (const k of keys) {
      const v = f[k];
      if (v === "" || v === undefined || v === null) continue;
      body[k] = v;
    }
    if (!Object.keys(body).length) return setToast("error", "Değişiklik yok");
    dispatch(updateSetting(body));
  };

  const num = (v) => (Number(v) || 0).toString();

  const kayitYok = !setting;

  return (
    <>
      <PageHead
        title="Uygulama ayarları"
        sub="Uygulamanın temel davranışı, coin oranları ve servis anahtarları."
      />

      <Tiles
        items={[
          { label: "Uygulama", value: setting?.isAppActive ? "Açık" : "Kapalı" },
          { label: "Yayıncı payı", value: `%${num(setting?.diamondCharge)}` },
          { label: "Çekim oranı", value: `${fmt(setting?.diamondPerDollar)} elmas = 1 $` },
          { label: "Giriş ödülü", value: `${fmt(setting?.loginBonus)} coin` },
        ]}
      />

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          ["genel", "Genel"],
          ["coin", "Coin ve ücretler"],
          ["agora", "Agora"],
          ["yasal", "Yasal metinler"],
          ["bildirim", "Bildirim"],
        ]}
      />

      {kayitYok && <div className="badu-empty">Ayarlar yükleniyor…</div>}

      {!kayitYok && tab === "genel" && (
        <>
          <Toggle
            on={!!setting?.isAppActive}
            onClick={() => dispatch(handleSwitch("app"))}
            title="Uygulama açık"
            note="Kapatırsan kullanıcılar giriş yapamaz, bakım ekranı görür."
          />
          <Toggle
            on={!!setting?.isFake}
            onClick={() => dispatch(handleSwitch("fake"))}
            title="Sahte veri"
            note="Test amaçlı örnek kullanıcı ve yayınlar gösterilir. Yayında kapalı olmalı."
          />

          <Card
            title="Karşılama"
            note="Kullanıcı ilk girdiğinde gördüğü mesaj ve aldığı günlük ödül."
            onSave={() => save(["welcomeMessage", "loginBonus"])}
          >
            <Field title="Hoş geldin mesajı" wide note="Giriş ekranında ve ilk açılışta görünür.">
              <Text value={f.welcomeMessage} onChange={set("welcomeMessage")} placeholder="AYVO'ya hoş geldiniz" />
            </Field>
            <Field title="Günlük giriş ödülü (coin)" note="Her gün ilk girişte hesaba eklenir.">
              <Text type="number" value={f.loginBonus} onChange={set("loginBonus")} />
            </Field>
          </Card>

          <Card
            title="Yönlendirme"
            note="Uygulama kapatıldığında ya da güncelleme gerektiğinde gösterilen ekran."
            onSave={() => save(["redirectMessage", "redirectAppUrl"])}
          >
            <Field title="Yönlendirme mesajı" wide>
              <Text value={f.redirectMessage} onChange={set("redirectMessage")} />
            </Field>
            <Field title="Yönlendirilecek adres" wide note="Örnek: https://badulive.online">
              <Text value={f.redirectAppUrl} onChange={set("redirectAppUrl")} />
            </Field>
          </Card>
        </>
      )}

      {!kayitYok && tab === "coin" && (
        <>
          <Card
            title="Elmas oranları — yayıncı kazancı"
            note="Kullanıcı COIN satın alır ve hediye gönderir; yayıncı ELMAS kazanır ve elmasla çekim yapar. Bu iki değer kazancı doğrudan belirler, dikkatli değiştir."
            onSave={() => save(["diamondPerDollar", "diamondCharge", "withdrawLimit"])}
          >
            <Field
              title="Yayıncı payı (%)"
              note="Gelen hediyenin yüzde kaçı yayıncıya ELMAS olarak yazılır. Şu an %75."
            >
              <Text type="number" value={f.diamondCharge} onChange={set("diamondCharge")} />
            </Field>
            <Field
              title="Çekim oranı (elmas = 1 $)"
              note="Yayıncı para çekerken kaç elmas 1 dolar sayılır. Şu an 12.500. Örnek: 5.500 coinlik hediye ($1) → %75 = 4.125 elmas → $0,33."
            >
              <Text type="number" value={f.diamondPerDollar} onChange={set("diamondPerDollar")} />
            </Field>
            <Field title="En az çekim (elmas)" note="Bu tutarın altında çekim talebi açılamaz.">
              <Text type="number" value={f.withdrawLimit} onChange={set("withdrawLimit")} />
            </Field>
          </Card>

          <Card
            title="Eski coin oranları"
            note="Artık yalnızca koltuktaki normal kullanıcıya (yayıncı olmayan) hediye gelirse kullanılır. Yayıncı kazancı yukarıdaki elmas oranlarıyla hesaplanır."
            onSave={() => save(["coinPerDollar", "coinCharge"])}
          >
            <Field title="Kullanıcı payı (%)">
              <Text type="number" value={f.coinCharge} onChange={set("coinCharge")} />
            </Field>
            <Field title="Eski çekim oranı (coin = 1 $)">
              <Text type="number" value={f.coinPerDollar} onChange={set("coinPerDollar")} />
            </Field>
          </Card>

          <Card
            title="Arama ücretleri"
            note="Dakika başına kullanıcıdan düşülen coin."
            onSave={() => save(["chargeForRandomCall", "chargeForPrivateCall"])}
          >
            <Field title="Rastgele arama (coin / dk)">
              <Text type="number" value={f.chargeForRandomCall} onChange={set("chargeForRandomCall")} />
            </Field>
            <Field title="Özel arama (coin / dk)">
              <Text type="number" value={f.chargeForPrivateCall} onChange={set("chargeForPrivateCall")} />
            </Field>
          </Card>

          <div className="card">
            <div className="card-body" style={{ fontSize: 13, color: "#6B7280" }}>
              Uygulama içi satış fiyatları burada değil, <b>Coin paketleri</b> sayfasında. Web sitesi satışı ise{" "}
              <b>Web Sitesi</b> sayfasından yönetilir.
            </div>
          </div>
        </>
      )}

      {!kayitYok && tab === "agora" && (
        <Card
          title="Agora anahtarları"
          note="Canlı yayın, sesli oda ve aramalar bu anahtarlarla çalışır. Yanlış girilirse yayın açılmaz."
          onSave={() => save(["agoraKey", "agoraCertificate"])}
        >
          <Field title="App ID" wide>
            <Secret value={f.agoraKey} onChange={set("agoraKey")} placeholder="Agora App ID" />
          </Field>
          <Field title="App Certificate" wide>
            <Secret value={f.agoraCertificate} onChange={set("agoraCertificate")} placeholder="Agora Certificate" />
          </Field>
        </Card>
      )}

      {!kayitYok && tab === "yasal" && (
        <Card
          title="Yasal metinler"
          note="App Store incelemesinde bu bağlantılar kontrol edilir."
          onSave={() => save(["privacyPolicyLink", "privacyPolicyText", "termAndCondition"])}
        >
          <Field title="Gizlilik politikası bağlantısı" wide>
            <Text value={f.privacyPolicyLink} onChange={set("privacyPolicyLink")} placeholder="https://…" />
          </Field>
          <Field title="Gizlilik politikası metni" wide>
            <Area rows={6} value={f.privacyPolicyText} onChange={set("privacyPolicyText")} />
          </Field>
          <Field title="Kullanım koşulları" wide>
            <Area rows={6} value={f.termAndCondition} onChange={set("termAndCondition")} />
          </Field>
        </Card>
      )}

      {!kayitYok && tab === "bildirim" && (
        <Card
          title="Firebase bildirim anahtarı"
          note="Firebase konsolundan indirdiğin servis hesabı dosyasının içeriğini olduğu gibi yapıştır."
          onSave={() => {
            try {
              if (f.privateKey) JSON.parse(f.privateKey);
            } catch (e) {
              return setToast("error", "Geçersiz JSON — dosyayı olduğu gibi yapıştır");
            }
            save(["privateKey"]);
          }}
        >
          <Field title="Servis hesabı (JSON)" wide>
            <Area rows={12} value={f.privateKey} onChange={set("privateKey")} placeholder='{ "type": "service_account", … }' />
          </Field>
        </Card>
      )}
    </>
  );
};

export default AppSetting;
